<template>
  <b-modal
    :id="modalId"
    :title="$t('dispatchFleet.map.title', { team: teamName })"
    size="xl"
    centered
    hide-footer
    @shown="onShown"
    @hidden="onHidden"
  >
    <div class="tlm-bar">
      <span class="tlm-bar__code">{{ code }}</span>
      <template v-if="position">
        <span class="tlm-dot" :style="{ background: fleetColor(position) }" />
        <span>{{ fleetStateLabel(position) }} · {{ positionAgeLabel }}</span>
      </template>
      <span v-if="meta && meta.from" class="text-muted">
        {{ $t('dispatchFleet.map.since', { time: sinceTime }) }}
      </span>
      <span v-if="meta && meta.live" class="tlm-live">{{ $t('dispatchFleet.map.live') }}</span>
      <span v-if="meta && meta.truncated" class="text-muted small">{{ $t('dispatchFleet.map.simplified') }}</span>
    </div>

    <b-alert
      v-if="trackError"
      show
      variant="warning"
      class="mb-1"
    >
      <div class="alert-body">
        {{ trackError }}
      </div>
    </b-alert>

    <GmapMap
      ref="mapRef"
      :center="center"
      :zoom="14"
      map-type-id="roadmap"
      style="width: 100%; height: 480px; border-radius: 8px"
    >
      <GmapMarker
        v-if="occurrenceCoords"
        :position="occurrenceCoords"
        :title="$t('dispatchFleet.map.occurrence')"
        :icon="occurrencePinIcon(occurrence)"
        :z-index="100"
      />
      <GmapPolyline
        v-if="path.length > 1"
        :path="path"
        :options="polylineOptions"
      />
      <GmapMarker
        v-if="path.length"
        :position="path[0]"
        :label="{ text: 'E', color: '#fff', fontSize: '11px', fontWeight: '700' }"
        :title="$t('dispatchFleet.map.start')"
        :icon="startIcon"
        :z-index="20"
      />
      <FleetVehicleMarker
        v-if="position"
        :position="position"
        :title="fleetMarkerTitle(position)"
        :z-index="50"
        :trail="path"
        highlighted
      />
    </GmapMap>

    <p
      v-if="!loading && isLive && !position"
      class="text-muted mt-1 mb-0"
    >
      {{ $t('dispatchFleet.map.noPosition') }}
    </p>
    <p
      v-else-if="!loading && meta && !path.length"
      class="text-muted mt-1 mb-0"
    >
      {{ $t('dispatchFleet.map.noTrack') }}
    </p>
  </b-modal>
</template>

<script>
import moment from 'moment'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import FleetPositionsMixin from '@/mixins/FleetPositionsMixin'
import DispatchTrailMixin from '@/mixins/DispatchTrailMixin'
import OccurrencePinMixin from '@/mixins/OccurrencePinMixin'
import FleetVehicleMarker from '@/components/FleetVehicleMarker.vue'

const TRAIL_REFRESH_MS = 60000

export default {
  name: 'TeamLiveMapModal',

  components: { FleetVehicleMarker },

  mixins: [ApiErrorMixin, FleetPositionsMixin, DispatchTrailMixin, OccurrencePinMixin],

  props: {
    dispatch: { type: Object, required: true },
    occurrence: { type: Object, default: null },
  },

  data() {
    return {
      loading: false,
      trackError: null,
      refreshTimer: null,
      ageTimer: null,
      now: Date.now(),
      startIcon: {
        path: 0, // google.maps.SymbolPath.CIRCLE (valor 0): não depende de o Google já estar carregado aqui
        scale: 9,
        fillColor: '#28c76f',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
      polylineOptions: { strokeColor: '#7367f0', strokeOpacity: 0.9, strokeWeight: 4 },
    }
  },

  computed: {
    modalId() {
      return `team-live-map-${this.dispatch.id}`
    },
    teamName() {
      return this.dispatch.team ? this.dispatch.team.name : ''
    },
    code() {
      return this.dispatch.team ? this.dispatch.team.frotas_identifier_code : null
    },
    // Só enquanto a equipe está despachada: depois da liberação o mapa mostra apenas o trajeto que ela fez.
    isLive() {
      return Number(this.dispatch.status) === 1
    },
    position() {
      if (!this.isLive) return null

      return this.fleetPositionsOnMap.find(p => p.identifier_code === this.code) || null
    },
    // Recalculada por segundo a partir do horário da leitura; o `age_seconds` do mixin só anda a cada 30 s.
    positionAgeLabel() {
      const p = this.position
      const s = p && p.at ? Math.max(0, Math.floor((this.now - new Date(p.at).getTime()) / 1000)) : null

      return this.fleetAgeLabel({ age_seconds: s })
    },
    path() {
      return this.trailPath(this.code)
    },
    meta() {
      return this.trailMeta[this.code] || null
    },
    sinceTime() {
      return this.meta && this.meta.from ? moment(this.meta.from).format('HH:mm') : ''
    },
    occurrenceCoords() {
      const o = this.occurrence
      return o && o.latitude && o.longitude ? { lat: Number(o.latitude), lng: Number(o.longitude) } : null
    },
    center() {
      if (this.position) return { lat: Number(this.position.lat), lng: Number(this.position.lng) }

      return this.occurrenceCoords || { lat: -27.5954, lng: -48.548 }
    },
  },

  beforeDestroy() {
    clearInterval(this.refreshTimer)
    clearInterval(this.ageTimer)
  },

  methods: {
    show() {
      this.$bvModal.show(this.modalId)
    },

    async onShown() {
      this.loading = true
      this.trackError = null
      this.watchFleetPositions()

      await Promise.all([this.loadFleetPositions(), this.loadTrackWithError()])
      this.loading = false
      this.fit()

      // Rede de segurança: o trajeto se reconstrói do Frotas, caso algum evento tenha se perdido.
      this.refreshTimer = setInterval(() => this.loadTrackWithError(), TRAIL_REFRESH_MS)
      this.ageTimer = setInterval(() => { this.now = Date.now() }, 1000)
    },

    onHidden() {
      this.unwatchFleetPositions()
      clearInterval(this.refreshTimer)
      clearInterval(this.ageTimer)
      this.refreshTimer = null
      this.ageTimer = null
    },

    async loadTrackWithError() {
      if (!this.code) return
      const data = await this.loadTrail(this.dispatch.id, this.code)
      this.trackError = data === null ? this.$t('dispatchFleet.map.trackError') : null
    },

    // Posição nova da equipe: entra na linha. (Chamado pelo FleetPositionsMixin.)
    onFleetPositionsApplied(codes) {
      if (!this.code || !codes.includes(this.code) || !this.position) return

      this.appendTrailPoint(this.code, this.position.lat, this.position.lng)
    },

    async fit() {
      try {
        const map = await this.$refs.mapRef.$mapPromise
        const bounds = new window.google.maps.LatLngBounds()
        let n = 0
        const add = p => {
          bounds.extend(p)
          n += 1
        }

        if (this.occurrenceCoords) add(this.occurrenceCoords)
        this.path.forEach(add)
        if (this.position) add({ lat: Number(this.position.lat), lng: Number(this.position.lng) })

        if (n > 1) map.fitBounds(bounds, 60)
      } catch (e) {
        // mapa ainda carregando: o centro padrão já mostra a região
      }
    },
  },
}
</script>

<style scoped>
.tlm-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  margin-bottom: 10px;
  font-size: 0.92rem;
}

.tlm-bar__code {
  padding: 1px 8px;
  border-radius: 6px;
  background: rgba(115, 103, 240, 0.14);
  font-weight: 600;
}

.tlm-dot {
  width: 10px;
  height: 10px;
  margin-right: -8px;
  border-radius: 50%;
}

.tlm-live {
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(40, 199, 111, 0.16);
  color: #1d7a4a;
  font-size: 0.78rem;
  font-weight: 700;
}
</style>
